import { useRef } from 'react'
import { useModal } from './ModalContext'
import { Icon, Modal } from '@nespresso-design-system/components'
import useImpressionTracker from '@content/helpers/src/hooks/useImpressionTracker'
import styles from './EnhancedModal.module.scss'
import {
    componentInteraction,
    selectPromotion
} from '@content/helpers/src/gtmEvents.js'
import Link from '@commons/link/Link.tsx'
import CTA from '@commons/cta/CTA.tsx'
import { ModalImage } from './types'
import { CampaignData } from '@content/helpers/src/tracking.types'
import {
    CampaignProps,
    CTA as CTAType,
    Link as LinkType
} from '@commons/drupal.types'
import { PromotionDetails } from '@commons/types.ts'

type ModalHeaderProps = {
    images: ModalImage[]
}

type ListStyleType = 'numeric' | null

type ModalRendererProps = {
    list_style: ListStyleType
    promotion_details: PromotionDetails
    campaign: CampaignProps
    isSubComponent: boolean
}

type ModalContentProps = {
    list_style: ListStyleType
    images: ModalImage[]
    heading: string
    list: string[]
    cta: CTAType
    cta_secondary: CTAType
    link: LinkType
    campaign: CampaignData
    isSubComponent: boolean
}

const ModalHeader = ({ images }: ModalHeaderProps) => {
    const getContainerClass = () => {
        if (images.length === 1 && !images[0].title) {
            return styles.singleImageBackground
        }
        if (images.length === 1 && images[0].title) {
            return styles.singleImageWithText
        }
        return null
    }

    const backgroundStyle =
        images.length === 1 && !images[0].title
            ? { backgroundImage: `url(${images[0].image?.desktop?.src})` }
            : {}

    return (
        <div
            className={`${styles.modalHeader} ${getContainerClass()}`}
            style={backgroundStyle}>
            {images.length === 1 && images[0].title && (
                <div className={styles.imageWrapper}>
                    <img
                        src={images[0].image?.desktop?.src}
                        alt={images[0].image?.desktop?.alt}
                        className={styles.promoImage}
                    />
                    <h3 className={styles.title}>{images[0].title}</h3>
                    <p className={styles.subtitle}>{images[0].subtitle}</p>
                </div>
            )}

            {images.length === 2 && (
                <div className={styles.promoImageContainer}>
                    {images.map((img, index) => (
                        <div
                            key={index}
                            className={
                                styles.imageWrapper + ' ' + styles.twoImages
                            }>
                            <img
                                src={img.image?.desktop?.src}
                                alt={img.image?.desktop?.alt || ''}
                                className={styles.promoImage}
                            />
                            <h3 className={styles.title}>{img.title}</h3>
                            <p className={styles.subtitle}>{img.subtitle}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

const ModalContent = ({
    list_style,
    images,
    heading,
    list,
    cta,
    cta_secondary,
    link,
    campaign,
    isSubComponent
}: ModalContentProps) => {
    const openCTA = ({ label, index }) => {
        if (isSubComponent) {
            componentInteraction(
                campaign.position,
                'modal link click',
                `LINK- ${index} - ${label}`
            )
        } else {
            selectPromotion({
                cta_name: `LINK- ${index} - ${label}`,
                ...campaign
            })
        }
    }

    const modifiedLink = {
        ...link,
        config: {
            ...link.config,
            className: [link.config?.className].filter(Boolean).join(' ')
        }
    }

    function mergeCTAClassName<T extends { config: { className: string } }>(
        item: T,
        className: string
    ): T {
        return {
            ...item,
            config: {
                ...item.config,
                className: [item.config?.className, className]
                    .filter(Boolean)
                    .join(' ')
            }
        }
    }

    return (
        <>
            <ModalHeader images={images} />
            <div className={styles.modalContent}>
                <h2 className={styles.heading}>{heading}</h2>
                <RenderList list_style={list_style} list={list} />
                <div className={styles.terms}>
                    <p>{link.label && <Link {...modifiedLink} />}</p>
                </div>
                <div className={styles.buttonContainer}>
                    {cta?.label && (
                        <CTA
                            {...mergeCTAClassName(cta, styles.btnPrimary)}
                            onClick={e => {
                                e.preventDefault()
                                openCTA({ label: cta.label, index: 1 })
                                window.location.href = cta.link
                            }}
                        />
                    )}

                    {cta_secondary?.label && (
                        <CTA
                            {...mergeCTAClassName(
                                cta_secondary,
                                styles.btnSecondary
                            )}
                            onClick={e => {
                                e.preventDefault()
                                openCTA({
                                    label: cta_secondary.label,
                                    index: 2
                                })
                                window.location.href = cta_secondary.link
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

const RenderList = ({
    list = [],
    list_style
}: {
    list?: any[]
    list_style: ListStyleType
}) => {
    if (!list.length) return null

    return (
        <ul className={list_style === 'numeric' ? styles.numericList : ''}>
            {list.map(item => (
                <li key={item.id}>
                    {list_style !== 'numeric' && (
                        <Icon icon={item.icon} method="mask" />
                    )}
                    {item.text}
                </li>
            ))}
        </ul>
    )
}

const ModalRenderer = ({
    list_style = '',
    promotion_details,
    campaign,
    isSubComponent = true
}: ModalRendererProps) => {
    const { isOpen, close } = useModal()
    const containerRef = useRef(null)
    const campaignData = isSubComponent
        ? { ...campaign, position: 'fs_enhanced_modal' }
        : campaign

    useImpressionTracker(containerRef, campaignData)

    if (!promotion_details) return null

    const {
        promo_modal: {
            id,
            translations,
            headerImages,
            heading,
            list,
            cta,
            cta_secondary,
            link
        }
    } = promotion_details

    return (
        <div
            className={styles.enhancedModal}
            data-testid="modal"
            ref={containerRef}>
            <Modal
                id={id}
                isOpen={isOpen}
                onClose={close}
                heading=""
                closeBtnA11yLabel={translations?.closeBtnA11yLabel}
                size="small"
                contentSlot={
                    <ModalContent
                        list_style={list_style}
                        images={headerImages}
                        heading={heading}
                        list={list}
                        cta={cta}
                        cta_secondary={cta_secondary}
                        link={link}
                        campaign={campaignData}
                        isSubComponent={isSubComponent}
                    />
                }
            />
        </div>
    )
}

export default ModalRenderer
